import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageSquare, ArrowRight, Clock, Globe } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';
import AppDownload from '../components/AppDownload';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Mesajınız gönderildi! En kısa sürede size dönüş yapacağız.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  // Animasyon Varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <>
      {/* 1. HERO SECTION: Tutarlı Tasarım */}
      <section className="relative pt-32 pb-20 bg-primary-900 overflow-hidden">
        {/* Arka plan efektleri */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none">
            <div className="absolute top-[-20%] left-[10%] w-[500px] h-[500px] bg-primary-500 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] right-[-5%] w-[400px] h-[400px] bg-white rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-display font-bold mb-6 text-white tracking-tight"
          >
            Bize Ulaşın
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-primary-100 leading-relaxed"
          >
            Sorularınız, projeleriniz veya sadece bir merhaba için buradayız. <br/>
            Lotus Sport ekibi ile iletişime geçin.
          </motion.p>
        </div>
      </section>

      {/* 2. İLETİŞİM KARTLARI */}
      <Section className="py-16 -mt-10 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* Telefon Kartı */}
          <motion.div
            className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-8 text-center border border-gray-100 hover:-translate-y-2 transition-transform duration-300"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="w-16 h-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
              <Phone size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Telefon</h3>
            <div className="space-y-2">
                <div className="flex items-center justify-center gap-2">
                    <span className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded">TR</span>
                    <a href="tel:+905326711570" className="text-lg font-medium text-gray-800 hover:text-blue-600 transition-colors">
                        +90 (532) 671 1570
                    </a>
                </div>
                <div className="flex items-center justify-center gap-2">
                    <span className="text-xs font-bold bg-gray-100 text-gray-600 px-2 py-1 rounded">NL</span>
                    <a href="tel:+31628864484" className="text-lg font-medium text-gray-800 hover:text-blue-600 transition-colors">
                        +31 6 2886 4484
                    </a>
                </div>
            </div>
            <p className="text-sm text-gray-400 mt-3">Hafta içi 09:00 - 18:00</p>
          </motion.div>

          {/* E-posta Kartı */}
          <motion.div
            className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-8 text-center border border-gray-100 hover:-translate-y-2 transition-transform duration-300"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="w-16 h-16 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mx-auto mb-6">
              <Mail size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">E-posta</h3>
            <a href="mailto:info@lotussport.net" className="text-lg font-medium text-gray-800 hover:text-primary-600 transition-colors">
                info@lotussport.net
            </a>
            <p className="text-sm text-gray-500 mt-3">Genellikle 12 saat içinde yanıtlanır</p>
          </motion.div>

          {/* Adres Kartı */}
          <motion.div
            className="bg-white rounded-2xl shadow-lg shadow-gray-200/50 p-8 text-center border border-gray-100 hover:-translate-y-2 transition-transform duration-300"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <div className="w-16 h-16 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center mx-auto mb-6">
              <MapPin size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Ofis</h3>
            <p className="text-lg font-medium text-gray-800">Entertech İstanbul Teknokent</p>
          </motion.div>

        </div>
      </Section>

      {/* 3. FORM VE DETAYLAR BÖLÜMÜ */}
      <Section className="py-20 bg-gray-50">
        <div className="flex flex-col lg:flex-row items-start gap-16">
          
          {/* Sol Taraf: Bilgilendirme */}
          <div className="lg:w-5/12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span className="text-primary-600 font-bold tracking-wider uppercase text-sm">İLETİŞİM FORMU</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mt-2 mb-6">
                Size Nasıl Yardımcı Olabiliriz?
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Aşağıdaki formu doldurarak bize ulaşabilirsiniz. Ekibimiz mesajınızı inceleyip en kısa sürede size dönüş yapacaktır.
              </p>
              
              <div className="space-y-6">
                {/* Destek Kartı */}
                <div className="flex items-start p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                  <div className="bg-purple-50 p-3 rounded-lg text-purple-600 mr-4">
                    <MessageSquare size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Teknik Destek</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Uygulama ile ilgili sorunlar için: <br/>
                      <a href="mailto:support@lotussport.net" className="text-primary-600 hover:underline font-medium">support@lotussport.net</a>
                    </p>
                  </div>
                </div>
                
                {/* İş Birliği Kartı */}
                <div className="flex items-start p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
                  <div className="bg-blue-50 p-3 rounded-lg text-blue-600 mr-4">
                    <Send size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">İş Ortaklığı</h3>
                    <p className="text-gray-600 text-sm mt-1">
                      Kurumsal çözümler ve partnerlik için: <br/>
                      <a href="mailto:info@lotussport.net" className="text-primary-600 hover:underline font-medium">info@lotussport.net</a>
                    </p>
                  </div>
                </div>

                {/* Çalışma Saatleri */}
                <div className="flex items-center gap-3 text-gray-500 text-sm mt-4 pl-2">
                    <Clock size={16} />
                    <span>Yanıt süresi ortalama 12 saattir.</span>
                </div>

              </div>
            </motion.div>
          </div>
          
          {/* Sağ Taraf: Form */}
          <motion.div
            className="lg:w-7/12 bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-8 md:p-10 border border-gray-100"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Adınız Soyadınız</label>
                    <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Adınız"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all focus:bg-white"
                    />
                </div>
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">E-posta Adresi</label>
                    <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="ornek@email.com"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all focus:bg-white"
                    />
                </div>
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Konu</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="Mesajınızın konusu"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all focus:bg-white"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Mesajınız</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Bize iletmek istediğiniz mesaj..."
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all focus:bg-white resize-none"
                />
              </div>
              
              <Button
                type="submit"
                variant="primary"
                className="w-full py-4 text-lg rounded-xl shadow-lg shadow-primary-600/30 hover:shadow-primary-600/50 transition-all"
                icon={<Send size={20} />}
              >
                Mesajı Gönder
              </Button>
            </form>
          </motion.div>
        </div>
      </Section>

      {/* 4. MAP SECTION */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="rounded-3xl overflow-hidden shadow-2xl shadow-gray-200 border-4 border-white h-[450px] relative"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Google Maps Entegrasyonu */}
            <iframe
              title="Lotus Sport Office Location"
              src="https://www.google.com/maps?q=40.99571262749126,28.723361010710622&z=17&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.2)" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            
            <div className="absolute bottom-6 left-6 bg-white p-4 rounded-xl shadow-lg hidden md:block">
                <p className="font-bold text-gray-900">Lotus Sport Merkez Ofis</p>
                <p className="text-xs text-gray-500">Entertech İstanbul Teknokent</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Download App Section */}
      <AppDownload />
    </>
  );
};

export default Contact;